import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ALLOWED_FILE_EXTENSIONS, ALLOWED_FILE_MIMES } from '@constants/constants';
import {
  enumDocumentRelatedEntityType,
  enumDocumentRootEntityType,
  enumDocumentState,
  enumDocumentType,
} from '@data-layer/enums/documents';
import { finalize, map, take, takeUntil } from 'rxjs/operators';
import { UntypedFormControl } from '@angular/forms';
import { CustomValidator } from '@helpers/custom-validator';
import { environment } from '@env/environment';
import { Observable, ReplaySubject } from 'rxjs';
import { DocumentCreateModel } from '@data-layer/models/documents';
import { DocumentsService } from '@services/documents.service';
import { PaginatedResultModel } from '@data-layer/models/common';
import { ManagementDocumentModel } from '@data-layer/models/management';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { TrackByService } from '@app/shared/services';

@Component({
  selector: 'app-container-documents',
  templateUrl: './container-documents.component.html',
  styleUrls: ['./container-documents.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContainerDocumentsComponent implements OnInit, OnDestroy {
  @Input() public containerId: number;
  @Output() public dataSaved: EventEmitter<void> = new EventEmitter<void>();
  @Output() public dataUpdated: EventEmitter<void> = new EventEmitter<void>();

  public attachedDocuments: ManagementDocumentModel[] = [];
  public availableDocTypes: enumDocumentType[];
  public currentClickedType: enumDocumentType;
  public enumDocumentType: typeof enumDocumentType = enumDocumentType;
  public ALLOWED_FILE_MIMES: typeof ALLOWED_FILE_MIMES = ALLOWED_FILE_MIMES;
  public fileControl: UntypedFormControl = new UntypedFormControl(null, [
    CustomValidator.fileExtensions(ALLOWED_FILE_EXTENSIONS),
    CustomValidator.maxFileSize(environment.files.default_file_size_limit),
  ]);
  public errors: IErrorForm = { maxFileSize: 'FORM_ERROR.MAX_FILE_SIZE', fileExtensionNotAllowed: 'FORM_ERROR.FILE_EXTENSION_NOT_ALLOWED' };
  public isLoading: boolean;

  public isShowErrorModalOpened: boolean;

  public trackByFn: (item: any) => string | number | string = TrackByService.trackById;

  @ViewChild('input', { static: true }) public inputRef: ElementRef;

  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>();

  constructor(private documentsService: DocumentsService, private changeDetector: ChangeDetectorRef) {}

  public ngOnInit(): void {
    this.availableDocTypes = [
      enumDocumentType.DRAFT_MASTER_BILL_OF_LADING,
      enumDocumentType.SHIPPING_ORDER,
      enumDocumentType.DEBIT_NOTE,
      enumDocumentType.LOADING_PLAN,
      enumDocumentType.MANIFEST,
      enumDocumentType.MASTER_BILL_OF_LADING,
      enumDocumentType.TELEX_RELEASE,
      enumDocumentType.FREIGHT_OTHER,
    ];

    this.getAllDocs();
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  public onAddOtherDocument(): void {
    this.currentClickedType = enumDocumentType.FREIGHT_OTHER;
    this.inputRef.nativeElement.click();
  }

  public handleFiles(event: Event): void {
    const target: HTMLInputElement = event.target as HTMLInputElement;
    const files: FileList = target.files;
    if (files && files.length) {
      this.isLoading = true;
      this.uploadFile(files[0], (file: File) => {
        this.uploadDocument(
          {
            relatedEntityId: this.containerId,
            relatedEntityType: enumDocumentRelatedEntityType.CONTAINER,
            rootEntityType: enumDocumentRootEntityType.CONTAINER,
            rootEntityId: this.containerId,
            type: this.currentClickedType,
            fileName: file.name,
          },
          file,
        )
          .pipe(
            takeUntil(this.destroy$),
            finalize(() => (this.isLoading = false)),
          )
          .subscribe(() => this.getAllDocs());
      });
    }
    this.inputRef.nativeElement.value = '';
  }

  public uploadDocument(document: DocumentCreateModel, file: File): Observable<any> {
    return this.documentsService.uploadManagementDocument(document, file);
  }

  public uploadDocumentCall(document: DocumentCreateModel, file: File): void {
    this.uploadDocument(document, file)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.getAllDocs();
        this.dataUpdated.emit();
      });
  }

  public removeDocument(documentId: number): void {
    this.documentsService
      .removeDocument(documentId, true)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.getAllDocs();
        this.dataUpdated.emit();
      });
  }

  public onDocumentStateUpdate(documentId: number, state: enumDocumentState): void {
    this.attachedDocuments = this.attachedDocuments.map((document: ManagementDocumentModel) =>
      document.id === documentId
        ? { ...document, state, ...(state === enumDocumentState.UPLOADED && { uploadedDate: new Date().toISOString() }) }
        : document,
    );

    this.dataUpdated.emit();
  }

  private getAllDocs(): void {
    this.loadDocuments(false)
      .pipe(take(1))
      .subscribe((docs: ManagementDocumentModel[]) => {
        this.attachedDocuments = this.filterFiles(docs);
        this.changeDetector.detectChanges();
      });
  }

  private uploadFile(file: File, callBack: (file: File) => void): void {
    this.clearFile();
    this.fileControl.patchValue(file);
    this.fileControl.markAsTouched();
    if (this.fileControl.valid) {
      this.isShowErrorModalOpened = true;
      callBack(file);
    } else {
      this.isShowErrorModalOpened = true;
    }
    this.inputRef.nativeElement.value = null;
  }

  private clearFile(): void {
    this.fileControl.patchValue(null);
    this.fileControl.markAsUntouched();
  }

  private loadDocuments(isShowLoader: boolean): Observable<ManagementDocumentModel[]> {
    return this.documentsService
      .getManagementDocuments({ rootEntityId: this.containerId, rootEntityType: enumDocumentRootEntityType.CONTAINER }, isShowLoader)
      .pipe(
        map((data: PaginatedResultModel<ManagementDocumentModel>) => data.items),
        takeUntil(this.destroy$),
      );
  }

  private filterFiles(documents: ManagementDocumentModel[], isReload?: boolean): ManagementDocumentModel[] {
    // add docs of this type to the beginning (create empty if none)
    // sort them via fake-id or created id
    // & create result array
    const docsToAdd: enumDocumentType[] = [
      enumDocumentType.DRAFT_MASTER_BILL_OF_LADING,
      enumDocumentType.SHIPPING_ORDER,
      enumDocumentType.DEBIT_NOTE,
      enumDocumentType.LOADING_PLAN,
      enumDocumentType.MANIFEST,
      enumDocumentType.MASTER_BILL_OF_LADING,
      enumDocumentType.TELEX_RELEASE,
      enumDocumentType.FREIGHT_OTHER,
    ];
    const tempDocs: ManagementDocumentModel[] = documents.filter(
      (document: ManagementDocumentModel) => document.state !== enumDocumentState.DELETED,
    );

    docsToAdd.forEach((documentType: enumDocumentType, index: number) => {
      if (
        !documents.find(
          (document: ManagementDocumentModel) => document.type === documentType && document.state !== enumDocumentState.DELETED,
        )
      ) {
        tempDocs.push({
          relatedEntityId: this.containerId,
          type: documentType,
          relatedEntityType: enumDocumentRelatedEntityType.CONTAINER,
          rootEntityType: enumDocumentRootEntityType.CONTAINER,
          rootEntityId: this.containerId,
          createdDate: null,
          id: index,
          fileName: null,
          state: enumDocumentState.CREATED,
        });
      }
    });

    return [
      ...tempDocs.filter((doc: ManagementDocumentModel) => doc.type === enumDocumentType.DRAFT_MASTER_BILL_OF_LADING),
      ...tempDocs.filter((doc: ManagementDocumentModel) => doc.type === enumDocumentType.SHIPPING_ORDER),
      ...tempDocs.filter((doc: ManagementDocumentModel) => doc.type === enumDocumentType.DEBIT_NOTE),
      ...tempDocs.filter((doc: ManagementDocumentModel) => doc.type === enumDocumentType.LOADING_PLAN),
      ...tempDocs.filter((doc: ManagementDocumentModel) => doc.type === enumDocumentType.MANIFEST),
      ...tempDocs.filter((doc: ManagementDocumentModel) => doc.type === enumDocumentType.MASTER_BILL_OF_LADING),
      ...tempDocs.filter((doc: ManagementDocumentModel) => doc.type === enumDocumentType.TELEX_RELEASE),
      ...tempDocs.filter((doc: ManagementDocumentModel) => doc.type === enumDocumentType.FREIGHT_OTHER),
    ];
  }
}
